/* eslint-disable react/only-export-components */
'use client'

import type { BreadcrumbItem } from '@/components/ui/breadcrumb'
import { usePathname, useRouter } from 'next/navigation'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

type Trail = { pathname: string; items: readonly BreadcrumbItem[] }
type NavigationContext = {
  register: (trail: Trail) => void
  popBreadcrumbItem: (pathname: string) => boolean
  parent: Trail | null
}

const BreadcrumbNavigationContext = createContext<NavigationContext | null>(null)

export function BreadcrumbNavigationProvider({ children }: { children: ReactNode }) {
  const [parent, setParent] = useState<Trail | null>(null)
  const active = useRef<Trail | null>(null)
  const popPending = useRef(false)

  const register = useCallback(
    (trail: Trail) => {
      if (popPending.current) {
        if (trail.pathname === active.current?.pathname) return
        active.current = parent
        popPending.current = false
      }
      if (active.current && active.current.pathname !== trail.pathname) {
        setParent(active.current)
      }
      active.current = trail
    },
    [parent],
  )
  const popBreadcrumbItem = useCallback((pathname: string) => {
    if (active.current?.pathname !== pathname) return false
    popPending.current = true
    return true
  }, [])
  const value = useMemo(() => ({ register, popBreadcrumbItem, parent }), [register, popBreadcrumbItem, parent])

  return <BreadcrumbNavigationContext value={value}>{children}</BreadcrumbNavigationContext>
}

export function useBreadcrumbNavigation(fallback: readonly BreadcrumbItem[]) {
  const context = useContext(BreadcrumbNavigationContext)
  const pathname = usePathname()
  const router = useRouter()
  const source = context?.parent
  const sourceItem = source?.items.at(-1)
  const sourceHref = sourceItem?.href ?? source?.pathname
  const ancestorIndex = source?.items.findIndex((item) => item.href === pathname) ?? -1
  let items = fallback.slice(0, -1)
  if (source?.pathname === pathname) {
    items = source.items.slice(0, -1)
  } else if (source && ancestorIndex >= 0) {
    items = source.items.slice(0, ancestorIndex)
  } else if (source && sourceHref && sourceHref !== pathname && sourceItem) {
    items = [
      ...source.items.slice(0, -1).filter((item) => item.href || item.onClick),
      { label: sourceItem.label, href: sourceHref },
    ]
  }
  const parent = items.at(-1)

  return {
    items,
    back: () => {
      if (parent?.href) router.push(parent.href)
      else parent?.onClick?.()
    },
  }
}

export function useRegisterBreadcrumb(items: readonly BreadcrumbItem[]) {
  const context = useContext(BreadcrumbNavigationContext)
  const pathname = usePathname()
  useEffect(() => {
    context?.register({ pathname, items })
  }, [context, pathname, items])
}

export function useReplaceAndPopBreadcrumb() {
  const context = useContext(BreadcrumbNavigationContext)
  const pathname = usePathname()
  const router = useRouter()
  return (href: string) => {
    if (context && !context.popBreadcrumbItem(pathname)) return
    router.replace(href)
  }
}
